import React, { useState, useEffect } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../Environment/firebase";
import {
  Typography,
  TextField,
  Grid,
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { departments} from "../../Environment/Config";
import { isMobile } from "react-device-detect";

const DiseaseEdit = () => {
  const { diseaseId } = useParams<{ diseaseId: string }>();
  const [diseaseData, setDiseaseData] = useState({
    name: "",
    caseList: [""],
    tag: "",
    furigana: "",
  });
  const [caseErrors, setCaseErrors] = useState<string[]>([""]);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchDiseaseData = async () => {
      if (!diseaseId) {
        setMessage("病名のIDが指定されていません。");
        return;
      }

      try {
        const docRef = doc(db, "search", diseaseId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setDiseaseData({
            name: data.name || "",
            caseList: data.case_list.length ? data.case_list : [""],
            tag: data.tag || "",
            furigana: data.furigana || "",
          });
        } else {
          setMessage("指定された病名が見つかりませんでした。");
        }
      } catch (error) {
        console.error("データの取得エラー:", error);
        setMessage("データの取得中にエラーが発生しました。");
      }
    };

    fetchDiseaseData();
  }, [diseaseId]);

  const handleBack = () => {
    navigate(-1); // -1は前のページに戻ることを意味します
  };

  const handleInputChange = (field: string, value: string) => {
    setDiseaseData((prevData) => ({ ...prevData, [field]: value }));
  };

  const handleCaseListChange = (index: number, value: string) => {
    setDiseaseData((prevData) => {
      const updatedCaseList = [...prevData.caseList];
      updatedCaseList[index] = value;
      return { ...prevData, caseList: updatedCaseList };
    });
  };

  const addCase = () => {
    setDiseaseData((prevData) => ({
      ...prevData,
      caseList: [...prevData.caseList, ""],
    }));
    setCaseErrors((prevErrors) => [...prevErrors, ""]);
  };

  const removeCase = (index: number) => {
    setDiseaseData((prevData) => {
      const updatedCaseList = [...prevData.caseList];
      updatedCaseList.splice(index, 1);
      return { ...prevData, caseList: updatedCaseList };
    });
    setCaseErrors((prevErrors) => {
      const updatedErrors = [...prevErrors];
      updatedErrors.splice(index, 1);
      return updatedErrors;
    });
  };

  const validateCaseList = async () => {
    const caseList = diseaseData.caseList;
    const errors = [...caseErrors];

    const promises = caseList.map(async (caseItem, index) => {
      if (!caseItem.trim()) {
        // 空欄の場合にエラーを設定
        errors[index] = "このフィールドは空白にできません";
        return false;
      }
      const caseQuery = query(
        collection(db, "dwh"),
        where("share", "in", ["true", "mnist"]),
        where("id", "==", caseItem)
      );
      const querySnapshot = await getDocs(caseQuery);

      if (querySnapshot.empty) {
        errors[index] = "このIDは存在しません";
        return false;
      } else {
        errors[index] = "";
        return true;
      }
    });

    const results = await Promise.all(promises);
    setCaseErrors(errors);

    return results.every((exists) => exists);
  };

  const saveDiseaseData = async () => {
    if (!diseaseId) return;

    if (!diseaseData.name) {
      setNameError(true);
      setMessage("病名は必須項目です。");
      return;
    } else {
      setNameError(false);
    }

    const allCasesValid = await validateCaseList();
    if (!allCasesValid) {
      setMessage("典型例のエラーを修正してください。");
      return;
    }

    setIsSaving(true);

    try {
      const docRef = doc(db, "search", diseaseId);
      await updateDoc(docRef, {
        name: diseaseData.name,
        case_list: diseaseData.caseList,
        tag: diseaseData.tag,
        furigana: diseaseData.furigana,
      });
      setMessage("データが保存されました。");
      navigate("/manage/catalogue/disease");
    } catch (error) {
      console.error("データの保存エラー:", error);
      setMessage("データの保存中にエラーが発生しました。");
    } finally {
      setIsSaving(false);
    }
  };

  const deleteDiseaseData = async () => {
    if (!diseaseId) return;

    const isConfirmed = window.confirm(
      "本当に削除しますか？ この操作は取り消せません。"
    );
    if (!isConfirmed) return;

    setIsSaving(true);

    try {
      const docRef = doc(db, "search", diseaseId);
      await deleteDoc(docRef);
      setMessage("データが削除されました。");
      navigate("/manage/catalogue/disease");
    } catch (error) {
      console.error("データの削除エラー:", error);
      setMessage("データの削除中にエラーが発生しました。");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid
        item
        display={"block"}
        xs={12}
        sm={7}
        mx={3}
        sx={{ mt: 3, minHeight: "95vh", textAlign: "center" }}
      >
        {message && (
          <Typography variant="body1" color="error" sx={{ mt: 2 }}>
            {message}
          </Typography>
        )}
        <Grid item>
          <TextField
            label="病名"
            variant="outlined"
            fullWidth
            required
            value={diseaseData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={nameError}
            helperText={nameError ? "病名は必須項目です。" : ""}
            sx={{ my: 5 }}
          />
          <Grid
            container
            sx={{
              display: "Grid",
              alignItems: "center",
              justifyContent: "center",
              gridTemplateColumns: "50% 50%",
              width: "100%",
              maxWidth: "100%",
              columnGap: 1,
              mb: 5,
            }}
          >
            <Grid item>
              <FormControl fullWidth required sx={{ my: 1 }}>
                <TextField
                  value={diseaseData.furigana}
                  onChange={(e) =>
                    handleInputChange("furigana", e.target.value as string)
                  }
                  label="ふりがな"
                />
                
              </FormControl>
            </Grid>
            <Grid item>
              <FormControl fullWidth required sx={{ my: 1 }}>
                <InputLabel id="tag-label">タグ</InputLabel>
                <Select
                  labelId="tag-label"
                  value={diseaseData.tag}
                  onChange={(e) =>
                    handleInputChange("tag", e.target.value as string)
                  }
                  label="タグ"
                >
                  {departments.map((tag) => (
                    <MenuItem key={tag} value={tag}>
                      {tag}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="h6" sx={{ mt: 2 }}>
            典型例
          </Typography>
          <Grid container flexDirection="column">
            <Grid item>
              {diseaseData.caseList.map((caseItem, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  sx={{ my: 1 }}
                >
                  <TextField
                    label={`症例 ${index + 1}`}
                    placeholder="典型例のurlの最後のパスを入力してください"
                    variant="outlined"
                    fullWidth
                    value={caseItem}
                    onChange={(e) =>
                      handleCaseListChange(index, e.target.value)
                    }
                    error={Boolean(caseErrors[index])}
                    helperText={caseErrors[index]}
                  />

                  {diseaseData.caseList.length > 1 && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => removeCase(index)}
                      sx={{ ml: 1 }}
                    >
                      削除
                    </Button>
                  )}
                </Box>
              ))}
            </Grid>
            <Grid
              item
              justifyContent="start"
              sx={{
                width: "100%",
                maxWidth: "100%",
                display: "flex",
              }}
            >
              <Button variant="outlined" color="primary" onClick={addCase}>
                追加
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            columnGap: 1,
            justifyContent: "center",
          }}
        >
          <Grid item>
            <Button 
            onClick={handleBack} 
            sx={{ mt: 2 }}
            variant="outlined"
             >
              キャンセル
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={saveDiseaseData}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              保存
            </Button>
          </Grid>
        </Grid>
        <Grid container sx={{ justifyContent: "center", mt: 4 }}>
          <Grid item>
            <Button
              variant="contained"
              color="error"
              onClick={deleteDiseaseData}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              疾患を削除
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DiseaseEdit;
